import sum from "lodash/sum";
import type { DoorLoopLeadDto } from "@/dto";
import type { DLMCCookie } from "./types";

const defaultField = "Other/Missing/Null";

const sourceScores = {
  [defaultField]: 2,
  ig: 1,
  fb: 2,
  capterra: 2,
  GetApp: 3,
  "SoftwareAdvice.com": 3,
  bing: 6,
  blog: 6,
  "google-ads": 9,
  null: 9,
  direct: 20
};

const timelineScores = {
  [defaultField]: 10,
  "6 Months +": 5,
  "3-6 Months": 10,
  "1-3 Months": 15,
  ASAP: 20
};

const propertyTypeScores = {
  [defaultField]: 6,
  "Short term rentals": 0,
  Storage: 0,
  Commercial: 4,
  "Community Associations": 6,
  "Affordable Housing": 6,
  Residential: 7,
  Both: 10,
  "Residential & Commercial": 10
};

const propertyManagerTypeScores = {
  [defaultField]: 6,
  Landlord: 4,
  "Property Owner": 7,
  "Property Manager": 10,
  "Management Company Owner": 10
};

const getUnitsScore = (units: number): number => {
  // Guard against NaN, Infinity, negatives — defaults to the 0-units bucket.
  if (!Number.isFinite(units) || units <= 0) return 10;
  if (units <= 10) return 20;
  if (units <= 50) return 40;
  if (units <= 300) return 20;
  if (units <= 2000) return 20;
  return 5;
};

const getKeyValue = (haystack: Record<string, number>, key?: string) => {
  const lookupKey = key ?? "$$$$";
  return haystack[lookupKey] ?? haystack[defaultField];
};

export const QUALIFIED_LEAD_SCORE_THRESHOLD = 33;

export interface LeadScoreBreakdown {
  total: number;
  passed: boolean;
  components: {
    source: number;
    timeline: number;
    propertyType: number;
    role: number;
    units: number;
  };
  inputs: {
    utmSource: string | undefined;
    whenPlanToUseSoftware: string | undefined;
    mainPropertyType: string | undefined;
    selectedJobTitle: string | undefined;
    numberOfUnits: number;
  };
}

export const computeLeadScoreBreakdown = (dto: DoorLoopLeadDto, cookieData: DLMCCookie): LeadScoreBreakdown => {
  const numberOfUnitsRaw = Number(dto.numberOfUnits ?? 0);
  const numberOfUnits = Number.isFinite(numberOfUnitsRaw) ? numberOfUnitsRaw : 0;

  const components = {
    source: getKeyValue(sourceScores, cookieData?.utmSource),
    timeline: getKeyValue(timelineScores, dto.whenPlanToUseSoftware),
    propertyType: getKeyValue(propertyTypeScores, dto.mainPropertyType),
    role: getKeyValue(propertyManagerTypeScores, dto.selectedJobTitle),
    units: getUnitsScore(numberOfUnits)
  };
  const total = sum(Object.values(components));

  return {
    total,
    passed: total >= QUALIFIED_LEAD_SCORE_THRESHOLD,
    components,
    inputs: {
      utmSource: cookieData?.utmSource,
      whenPlanToUseSoftware: dto.whenPlanToUseSoftware,
      mainPropertyType: dto.mainPropertyType,
      selectedJobTitle: dto.selectedJobTitle,
      numberOfUnits
    }
  };
};

/**
 * Pure function — does NOT fire analytics events. Callers fire
 * `lead_passed_lead_scoring` / `lead_failed_lead_scoring` exactly once at
 * form submission. Previously this function fired the event itself, which
 * resulted in 2-3× duplicate events per submit because calculateLeadScore
 * was invoked from multiple call sites in the same submit handler.
 */
export const calculateLeadScore = (dto: DoorLoopLeadDto, cookieData: DLMCCookie): number =>
  computeLeadScoreBreakdown(dto, cookieData).total;
